import { useEffect, useRef, useState } from 'react';
import { attemptKey, loadAttempt, createAttempt, canClear, submittedCount } from '../models/enquiryAttempt';
import { executeEnquiryAttempt, enquiryInFlight, recoverEnquiry } from '../models/enquiryBackground';

export function useEnquiryAttempt(user, query, open, onCompleted, onFailure, onStarted, onAlreadySending) {
  const scope = attemptKey(user, query);
  const [state, setState] = useState({ scope, attempt: null, busy: false });
  const current = useRef({});
  current.current = { scope, open, onCompleted, onFailure, onStarted, onAlreadySending };
  useEffect(() => {
    const read = () => setState({ scope, attempt: loadAttempt(user, query), busy: enquiryInFlight(user, query) });
    read(); window.addEventListener('storage', read); window.addEventListener('enquiry-attempt-updated', read);
    return () => { if (current.current.scope === scope) current.current = {}; window.removeEventListener('storage', read); window.removeEventListener('enquiry-attempt-updated', read); };
  }, [scope, user, query]);
  function execute(saved, fresh = false) {
    setState({ scope, attempt: saved, busy: true });
    try {
      const pending = executeEnquiryAttempt(saved, fresh ? () => {
        if (current.current.scope === scope && current.current.open) current.current.onStarted?.(saved);
      } : undefined, failure => {
        if (current.current.scope === scope && current.current.open) current.current.onFailure?.(failure);
      });
      pending.then(next => {
        if (current.current.scope !== scope) return;
        setState({ scope, attempt: next, busy: false });
        if (current.current.open) {
          if (canClear(next)) current.current.onCompleted?.(submittedCount(next));
        }
      });
      return pending;
    } catch {
      const next = { ...saved, status: 'blocked', storageBlocked: true, error: 'Cannot persist this send safely. Do not send again until the saved outcome is verified.' };
      setState({ scope, attempt: next, busy: false });
      return Promise.resolve(next);
    }
  }
  const attempt = state.scope === scope ? state.attempt : loadAttempt(user, query);
  function startLocked(payload, team) {
    if (current.current.scope !== scope || !current.current.open) return false;
    const saved = loadAttempt(user, query);
    if (saved || enquiryInFlight(user, query) || !user || !query) {
      if (enquiryInFlight(user, query) || ['processing', 'unknown'].includes(saved?.status)) current.current.onAlreadySending?.();
      return false;
    }
    execute(createAttempt(user, query, payload, team), true);
    return !!loadAttempt(user, query) && !loadAttempt(user, query)?.storageBlocked;
  }
  function start(payload, team = []) {
    if (navigator.locks?.request) return navigator.locks.request(scope, { ifAvailable: true }, lock => lock ? startLocked(payload, team) : false);
    return Promise.resolve(startLocked(payload, team));
  }
  function check() {
    const saved = loadAttempt(user, query);
    if (saved && !saved.storageBlocked && !['completed', 'rejected'].includes(saved.status)) return execute(saved);
  }
  function clear() {
    const saved = loadAttempt(user, query);
    if (enquiryInFlight(user, query) || saved?.storageBlocked || !(canClear(saved) || saved?.status === 'rejected')) return false;
    try { localStorage.removeItem(scope); window.dispatchEvent(new Event('enquiry-attempt-updated')); return true; } catch { return false; }
  }
  useEffect(() => {
    const saved = loadAttempt(user, query);
    if (open && saved && !saved.storageBlocked && ['processing', 'unknown'].includes(saved.status)) recoverEnquiry(user, query);
  }, [user, query, scope, open]);
  return { attempt, busy: state.scope === scope && state.busy, start, check, clear };
}
